import { computed } from "vue";
import { LetterFileColor } from "../dto/user-preferences.dto";
import { useUserPreferencesStore } from "../stores/user-preferences.store";

interface ColorDefinition {
  default: string;
  active: string;
  main: string;
  massSignature: {
    text: string;
    bg: string;
    border: string;
    tagBg: string;
  };
  pTab: {
    bg: string;
    borderTop: string;
  };
}

export const colorMap: Record<LetterFileColor, ColorDefinition> = {
  [LetterFileColor.BLUE]: {
    active: "bg-blue-cumulus-950-active",
    default: "bg-blue-cumulus-950",
    main: "#9fc3fc",
    massSignature: {
      text: "text-blue-cumulus-sun-368",
      bg: "bg-blue-cumulus-975",
      border: "border-blue-cumulus-850-active",
      tagBg: "bg-blue-cumulus-950",
    },
    pTab: {
      borderTop: "[&_.p-tag-button-active]:!border-t-blue-cumulus-950-active",
      bg: "[&_.p-tag-button]:bg-blue-cumulus-950",
    },
  },
  [LetterFileColor.GREEN]: {
    active: "bg-[#80d5c6]",
    default: "bg-[#bfeae2]",
    main: "#80d5c6",
    massSignature: {
      text: "text-[#1F4540]",
      bg: "bg-[#E0FBF6]",
      border: "border-[#80d5c6]",
      tagBg: "bg-[#A0EAE0]",
    },
    pTab: {
      borderTop: "[&_.p-tag-button-active]:!border-t-[#80d5c6]",
      bg: "[&_.p-tag-button]:bg-[#bfeae2]",
    },
  },
  [LetterFileColor.RED]: {
    active: "bg-orange-terre-battue-main-645-hover",
    default: "bg-orange-terre-battue-850",
    main: "#f1a892",
    massSignature: {
      text: "text-[#983500]",
      bg: "bg-[#FEF4F2]",
      border: "border-[#f60]",
      tagBg: "bg-[#FDDFD8]",
    },
    pTab: {
      borderTop: "[&_.p-tag-button-active]:!border-t-orange-terre-battue-main-645-hover",
      bg: "[&_.p-tag-button]:bg-orange-terre-battue-850",
    },
  },
  [LetterFileColor.YELLOW]: {
    active: "bg-green-tilleul-verveine-950",
    default: "bg-green-tilleul-verveine-975",
    main: "#fceeac",
    massSignature: {
      text: "text-[#B59300]",
      bg: "bg-[#fef7da]",
      border: "border-[#FFDA3D]",
      tagBg: "bg-[#F5EFD5]",
    },
    pTab: {
      borderTop: "[&_.p-tag-button-active]:!border-t-green-tilleul-verveine-950",
      bg: "[&_.p-tag-button]:bg-green-tilleul-verveine-975",
    },
  },
};

export function useLetterFileColor() {
  const userPreferencesStore = useUserPreferencesStore();
  const activeColorClass = computed(() => {
    return colorMap[userPreferencesStore.color || LetterFileColor.RED].active;
  });

  const defaultColorClass = computed(() => {
    return colorMap[userPreferencesStore.color || LetterFileColor.RED].default;
  });

  const mainFeatureColor = computed(() => {
    return colorMap[userPreferencesStore.color || LetterFileColor.RED].main;
  });

  const massSignatureColors = computed(() => {
    return colorMap[userPreferencesStore.color || LetterFileColor.RED].massSignature;
  });

  const pTabColors = computed(() => {
    return colorMap[userPreferencesStore.color || LetterFileColor.RED].pTab;
  });

  return {
    activeColorClass,
    defaultColorClass,
    mainFeatureColor,
    massSignatureColors,
    pTabColors,
  };
}
